import type {
  NetworkGraphEdgeModel,
  NetworkGraphNodeModel,
} from '../NetworkGraph';
import type { NetworkGraphPosition } from './computeRadialLayout';
import {
  edgeGeometry,
  reciprocalLaneOffsets,
  targetEndpointAngleOffsets,
} from './networkGraphEdgeUtils';
import type {
  LabelCircleObstacle,
  LabelSegmentObstacle,
} from './resolveLabelCollisions';

export interface NetworkGraphGeometry {
  nodeById: Map<string, NetworkGraphNodeModel>;
  connectedPositions: Map<string, NetworkGraphPosition[]>;
  edgeSegments: LabelSegmentObstacle[];
  arrowheadCircles: LabelCircleObstacle[];
  edgeLengths: Map<string, number>;
  targetAngleOffsets: Map<string, number>;
  laneOffsets: Map<string, number>;
}

export const buildGraphGeometry = (
  nodes: readonly NetworkGraphNodeModel[],
  edges: readonly NetworkGraphEdgeModel[],
  positions: Readonly<Record<string, NetworkGraphPosition>>
): NetworkGraphGeometry => {
  const nodeById = new Map(nodes.map(node => [node.id, node]));
  const connectedPositions = new Map<string, NetworkGraphPosition[]>();
  const edgeSegments: LabelSegmentObstacle[] = [];
  const arrowheadCircles: LabelCircleObstacle[] = [];
  const edgeLengths = new Map<string, number>();
  const targetAngleOffsets = targetEndpointAngleOffsets({
    edges,
    positions,
    nodeById,
  });
  const laneOffsets = reciprocalLaneOffsets(edges);

  for (const edge of edges) {
    const source = nodeById.get(edge.sourceId);
    const target = nodeById.get(edge.targetId);
    const sourcePosition = positions[edge.sourceId];
    const targetPosition = positions[edge.targetId];
    if (!source || !target || !sourcePosition || !targetPosition) {
      continue;
    }

    const sourceConnections = connectedPositions.get(edge.sourceId) ?? [];
    sourceConnections.push(targetPosition);
    connectedPositions.set(edge.sourceId, sourceConnections);
    const targetConnections = connectedPositions.get(edge.targetId) ?? [];
    targetConnections.push(sourcePosition);
    connectedPositions.set(edge.targetId, targetConnections);

    const geometry = edgeGeometry({
      source,
      sourcePosition,
      target,
      targetPosition,
      targetAngleOffset: targetAngleOffsets.get(edge.id),
      lateralOffset: laneOffsets.get(edge.id),
    });
    edgeLengths.set(edge.id, geometry.length);
    edgeSegments.push({ start: geometry.start, end: geometry.end });
    arrowheadCircles.push({ ...geometry.end, radius: 8 });
    // Reciprocal (twoWayArrow) edges paint a head at the start too; reserve
    // collision space for it so labels never overlap the second arrowhead.
    if (edge.twoWayArrow) {
      arrowheadCircles.push({ ...geometry.start, radius: 8 });
    }
  }

  return {
    nodeById,
    connectedPositions,
    edgeSegments,
    arrowheadCircles,
    edgeLengths,
    targetAngleOffsets,
    laneOffsets,
  };
};
